import React, { useState, useRef, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { 
  Upload, 
  Image as ImageIcon, 
  Sparkles, 
  Check, 
  X, 
  Loader2,
  FileImage,
  AlertCircle
} from 'lucide-react';
import { getErrorMessage } from '../utils/errorUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * ScreenshotExtractor Component
 * 
 * Allows users to:
 * 1. Upload a screenshot
 * 2. Extract key report information using OCR
 * 3. Review and edit extracted values
 * 4. Apply values to the current report
 */
export default function ScreenshotExtractor({ onApplyData, disabled = false }) {
  const { token } = useContext(AuthContext);
  const fileInputRef = useRef(null);
  
  // State
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [extractedData, setExtractedData] = useState({
    report_reference: '',
    purpose_of_valuation: '',
    rental_value: '',
    capital_value: ''
  });
  const [hasExtracted, setHasExtracted] = useState(false);
  
  // Allowed file types
  const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
  const MAX_SIZE_MB = 5;
  
  /**
   * Handle file selection
   */
  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Unsupported file type. Please upload PNG, JPG, or WEBP');
      return;
    }
    
    // Validate file size
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      toast.error(`File too large. Maximum size is ${MAX_SIZE_MB}MB`);
      return;
    }
    
    setSelectedFile(file);
    setHasExtracted(false);
    
    // Create preview URL
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
  };
  
  /**
   * Trigger file input click
   */
  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };
  
  /**
   * Extract data from uploaded screenshot
   */
  const handleExtract = async () => {
    if (!selectedFile) {
      toast.error('Please upload a screenshot first');
      return;
    }
    
    setExtracting(true);
    
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const response = await axios.post(
        `${API}/compliance/extract-screenshot`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      if (response.data.success) {
        setExtractedData(response.data.data);
        setHasExtracted(true);
        toast.success('Data extracted successfully');
      } else {
        toast.warning(response.data.message || 'No data could be extracted');
        setHasExtracted(true);
      }
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to extract data'));
    } finally {
      setExtracting(false);
    }
  };
  
  /**
   * Update an extracted field value
   */
  const handleFieldChange = (field, value) => {
    setExtractedData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  /**
   * Apply extracted data to the report
   */
  const handleApply = () => {
    if (onApplyData) {
      onApplyData(extractedData);
      toast.success('Values applied to report');
    }
  };
  
  /**
   * Clear all data and reset
   */
  const handleClear = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setHasExtracted(false);
    setExtractedData({
      report_reference: '',
      purpose_of_valuation: '',
      rental_value: '',
      capital_value: ''
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  // Check if any data has been entered
  const hasAnyData = Object.values(extractedData).some(v => v && v.trim());
  
  return (
    <Card className="border-dashed" data-testid="screenshot-extractor">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <FileImage className="h-4 w-4 text-blue-500" />
          Screenshot Extraction
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".png,.jpg,.jpeg,.webp"
          onChange={handleFileSelect}
          className="hidden"
          data-testid="screenshot-file-input"
        />
        
        {/* Upload Area */}
        {!previewUrl ? (
          <div
            onClick={handleUploadClick}
            className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-colors"
            data-testid="screenshot-upload-area"
          >
            <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
            <p className="text-sm text-gray-600">Click to upload screenshot</p>
            <p className="text-xs text-gray-400 mt-1">PNG, JPG, WEBP up to 5MB</p>
          </div>
        ) : (
          /* Image Preview */
          <div className="space-y-3">
            <div className="relative rounded-lg overflow-hidden border bg-gray-50">
              <img
                src={previewUrl}
                alt="Screenshot preview"
                className="w-full max-h-48 object-contain"
                data-testid="screenshot-preview"
              />
              <button
                onClick={handleClear}
                className="absolute top-2 right-2 p-1 bg-white/90 rounded-full hover:bg-red-100 transition-colors"
                data-testid="screenshot-remove-btn"
              >
                <X className="h-4 w-4 text-gray-600" />
              </button>
            </div>
            
            {/* Extract Button */}
            {!hasExtracted && (
              <Button
                onClick={handleExtract}
                disabled={extracting || disabled}
                className="w-full"
                size="sm"
                data-testid="screenshot-extract-btn"
              >
                {extracting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Extracting...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Extract Data
                  </>
                )}
              </Button>
            )}
          </div>
        )}
        
        {/* Extracted Fields */}
        {hasExtracted && (
          <div className="space-y-3 pt-2 border-t" data-testid="screenshot-fields">
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <AlertCircle className="h-3 w-3" />
              <span>Review and edit values before applying</span>
            </div>
            
            {/* Report Reference */}
            <div>
              <Label className="text-xs">Report Reference</Label>
              <Input
                value={extractedData.report_reference}
                onChange={(e) => handleFieldChange('report_reference', e.target.value)}
                placeholder="Enter report reference"
                className="h-8 text-sm"
                disabled={disabled}
                data-testid="screenshot-field-report-reference"
              />
            </div>
            
            {/* Purpose of Valuation */}
            <div>
              <Label className="text-xs">Purpose of Valuation</Label>
              <Input
                value={extractedData.purpose_of_valuation}
                onChange={(e) => handleFieldChange('purpose_of_valuation', e.target.value)}
                placeholder="Enter purpose"
                className="h-8 text-sm"
                disabled={disabled}
                data-testid="screenshot-field-purpose"
              />
            </div>
            
            {/* Rental Value */}
            <div>
              <Label className="text-xs">Rental Value (£)</Label>
              <Input
                value={extractedData.rental_value}
                onChange={(e) => handleFieldChange('rental_value', e.target.value)}
                placeholder="Enter rental value"
                className="h-8 text-sm"
                type="text"
                disabled={disabled}
                data-testid="screenshot-field-rental-value"
              />
            </div>
            
            {/* Capital Value */}
            <div>
              <Label className="text-xs">Capital Value (£)</Label>
              <Input
                value={extractedData.capital_value}
                onChange={(e) => handleFieldChange('capital_value', e.target.value)}
                placeholder="Enter capital value"
                className="h-8 text-sm"
                type="text"
                disabled={disabled}
                data-testid="screenshot-field-capital-value"
              />
            </div>
            
            {/* Action Buttons */}
            <div className="flex gap-2 pt-2">
              <Button
                onClick={handleApply}
                disabled={!hasAnyData || disabled}
                size="sm"
                className="flex-1"
                data-testid="screenshot-apply-btn"
              >
                <Check className="h-4 w-4 mr-1" />
                Apply to Report
              </Button>
              <Button
                onClick={handleClear}
                variant="outline"
                size="sm"
                disabled={disabled}
                data-testid="screenshot-clear-btn"
              >
                Clear
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
